import { useEffect, useState } from "react";
import { t } from "i18next";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import {
  paybyCheckPaymentStatus,
  paybyRefundRequest,
} from "apiHelpers/api/ApiHelpers";
import { errorHandler } from "apiHelpers/api/errorHandler";
import { ErrorAction } from "store/actions";
import { resetCart } from "utils/resetActions";
import { ErrorModal, CustomModal } from "components";
import useDineInSuccesOrder from "modules/dine-in/hooks/useSuccesOrder";
import useSuccesOrder from "features/hooks/useSuccesOrder";
import { useQueryParam } from "modules/dine-in/hooks";
import usePlaceOrderHelper from "features/payment-helpers/usePlaceOrderHelper";
import { useCustomHistory } from "hooks";
import PaymentConfirmationView from "./payment.complete";

const PaymentConfirmation = () => {
  const location = useLocation();
  const params = useParams();
  const router = useCustomHistory();
  const dispatch = useDispatch();
  const queryParams = queryString.parse(location.search);
  const [status, setStatus] = useState("PENDING");
  const [attempts, setAttempts] = useState(1);
  const [paymentRef] = useState(
    queryParams.paymentOrderNo || queryParams.transactionId
  );
  const [response, setResponse] = useState(null);
  const [path, setPath] = useState("/");
  const [isRefund, setIsRefund] = useState(false);
  // const [timer, setTimer] = useState(30); // half minutes in seconds
  const { DomainType, orderType } = useSelector((state) => state.AppReducer);
  const { brandInfo } = useSelector((state) => state.brandStorageReducer);
  const { selectedStore } = useSelector((state) => state.store);
  const { error, errorModaL } = useSelector((rstate) => rstate.errorSlice);
  const handleSuceessOrderDineInRoute = useDineInSuccesOrder();
  const handleSuceessOrderRoute = useSuccesOrder();
  const queryParam = useQueryParam();

  const handleSuccessOrderRoute = (orderNumber) => {
    const isDineIn = orderType.serviceType === "DINE IN";
    if (isDineIn) {
      handleSuceessOrderDineInRoute(orderNumber);
    } else {
      handleSuceessOrderRoute(orderNumber);
    }
  };

  const handleRouteRedirect = (url = "/") => {
    if (url === "/") {
      resetCart();
    }
    const isDineIn = orderType.serviceType === "DINE IN";
    if (isDineIn) {
      router(
        `/dine-in/${url}`,
        `/${params.domainName}/dine-in/${url}`,
        DomainType,
        null,
        `?${queryParam}`
      );
    } else {
      router(`/store${url}`, `/${params.domainName}/store/${url}`, DomainType);
    }
  };

  const { outlet_based_place_order, logError } = usePlaceOrderHelper(
    handleSuccessOrderRoute
  );

  const initialRefundRequest = async (payload) => {
    const filterPayload = payload || {
      merchantOrderNo: response?.merchantOrderNo,
      amount: response?.totalAmount,
    };
    if (isRefund) return;
    // let url = filterPayload && filterPayload?.merchantOrderNo && filterPayload?.totalAmount ? "/checkout" : "/";
    try {
      if (!filterPayload) {
        throw new Error("Order not found");
      }
      const apiResponse = await paybyRefundRequest({
        refundMerchantOrderNo: response?.orderNo,
        originMerchantOrderNo: filterPayload?.merchantOrderNo,
        amount: filterPayload?.totalAmount,
        reason:
          "Payment Status is not Paid Success or Settled even after 4 attempts",
      });
      let url = "/";
      let apiMessage = "Payment Process Failure";
      if (apiResponse?.data?.head?.applyStatus === "SUCCESS") {
        setIsRefund(true);
        if (apiResponse?.data?.head?.msg.includes("ORDER_NOT_PAID")) {
          url = "/checkout";
          apiMessage = "ORDER_NOT_PAID";
        }
        logError(t(apiMessage), true, "error");
      } else throw new Error(apiResponse?.data?.head?.msg);
    } catch (error) {
      logError(errorHandler(error), true, "error");
      setPath("/");
    } finally {
      setResponse(null);
    }
  };

  const checkPaymentStatusAndRedirect = async () => {
    if (isRefund || errorModaL) return;
    if (attempts >= 5) {
      initialRefundRequest({
        merchantOrderNo: response?.merchantOrderNo,
        totalAmount: response?.totalAmount,
      });
      return;
    }
    try {
      const apiResponse = await paybyCheckPaymentStatus({
        storeId: selectedStore?._id,
        brandId: brandInfo?.brand?._id,
        paymentOrderNo: paymentRef,
        merchantOrderNo: response?.merchantOrderNo,
        orderNo: response?.orderNo,
        isForceDelete: attempts === 4,
      });
      const status = apiResponse?.data?.data?.status?.toUpperCase();
      const isPaymentSuccess = ["SETTLED", "PAID_SUCCESS"].includes(status);
      setResponse(apiResponse?.data?.data?.orderData?.acquireOrder);

      if (apiResponse?.data?.success && isPaymentSuccess) {
        outlet_based_place_order({
          paymentIntent: apiResponse?.data?.data?.orderData?.acquireOrder,
        });
      } else if (attempts + 1 === 5) {
        initialRefundRequest({
          merchantOrderNo:
            apiResponse?.data?.data?.orderData?.acquireOrder?.merchantOrderNo,
          totalAmount:
            apiResponse?.data?.data?.orderData?.acquireOrder?.totalAmount,
        });
        return;
      } else {
        setStatus(status);
        setAttempts(prev => prev + 1);
      }
    } catch (error) {
      setStatus("ERROR");
      logError(errorHandler(error), true, "error");
      setPath("/");
    }
  };

  const handleCloseModal = () => {
    dispatch(ErrorAction.setErrorModal(false));
    handleRouteRedirect(path ?? "");
  };

  useEffect(() => {
    let timer;
  
    const isNotPaymentSuccess = status
      ? !["SETTLED", "PAID_SUCCESS", "ERROR"].includes(status)
      : true;
  
    if (isNotPaymentSuccess && attempts < 5 && !errorModaL && !isRefund) {
      timer = setTimeout(() => {
        checkPaymentStatusAndRedirect();
      }, 2000); // delay next retry by 2 seconds
    }
  
    return () => {
      clearTimeout(timer);
    };
  }, [status, attempts, errorModaL, isRefund]);


  return (
    <div className="store-page-popup">
      <PaymentConfirmationView
        status={status}
        // timer={timer}
        paymentRef={paymentRef}
      />
      <CustomModal
        open={errorModaL}
        reduxAction={handleCloseModal}
        bodyclasses="flex"
        overlayClasses="fixed h-screen w-screen top-0 bg-opacity-50 overflow-hidden transition ease-linear duration-700 z-40"
      >
        <ErrorModal reduxAction={handleCloseModal} message={error} />
      </CustomModal>
    </div>
  );
};

export default PaymentConfirmation;
